import { useEffect, useState } from 'react'

import cn from 'clsx'
import { format } from 'date-fns'
import { enUS } from 'date-fns/locale'

import { useAppDispatch, useAppSelector } from '@App/hooks'
import { selectEventById } from '@App/redux/slices/calendar/selectors'
import { EventMarket } from '@App/redux/slices/calendar/slice'
import { selectKlinesForMarket } from '@App/redux/slices/config/config.selectors'
import { setKlines } from '@App/redux/slices/config/ConfigSlice'
import {
  getTickerData,
  subscribeToPublicService,
  unsubscribeToPublicService,
} from '@App/redux/slices/sockets/PublicServiceConnectionSlice'
import { Events } from '@App/sockets/types'
import { CloseIcon } from '@App/static/icons/ArrowIcons'
import { Flag } from '@App/static/icons/Flags'
import { MarketIcon } from '@App/ui/crypto_exchange/desktop/components/common/MarketIcon'

import { TickerPrice } from '../../../discover/components/Fallers/components/TickerPrice'
import { useSeeMarket } from '../../../spot/components/Reports/model'
import { SparkLine } from '../../../spot/components/SpotOrderMarketsTable/components/SparkLine'
import { ImpactIndicator } from '../Filters/components/ImpactFilter/components/ImpactIndicator'

import s from './styles.module.scss'

const sparklineData = [100, 101, 103, 102, 104, 107, 110, 108, 112, 115, 118]
export const formatTimeBadge = (date: string | Date) => {
  const d = typeof date === 'string' ? new Date(date) : date
  return format(d, 'HH:mm, MMM d', { locale: enUS })
}

export const EventDetails = ({ eventId, onClose }: { eventId: number; onClose: () => void }) => {
  const event = useAppSelector((state) => selectEventById(state, eventId))

  return (
    <div className={s.container}>
      <div className={s.header}>Event details</div>
      <CloseIcon className={s.closeIcon} onClick={onClose} />

      {event && (
        <div className={s.content}>
          <div className={s.section}>
            <Flag code={event.country.key} className={s.flagInfo} />
            <div className={s.date}>{formatTimeBadge(event.datetime)}</div>
            <div>Impact</div>
            <div>
              <ImpactIndicator level={event.impact} />
            </div>
          </div>
          <div className={cn(s.section, s.title)}>{event.title}</div>
          <div className={s.section}>
            <div className={s.eventMetrics}>
              <div className={s.infoCell}>
                <span className={s.label}>Actual</span>
                <span className={s.value}>{event.metric.actual ?? '-'}</span>
              </div>
              <div className={s.infoCell}>
                <span className={s.label}>Forecast</span>
                <span className={s.value}>{event.metric.forecast ?? '-'}</span>
              </div>
              <div className={s.infoCell}>
                <span className={s.label}>Prior</span>
                <span className={s.value}>{event.metric.prior ?? '-'}</span>
              </div>
            </div>
          </div>
          <div className={s.relatedMarkets}>
            <div className={s.title}>Related markets</div>
            {event.relatedMarkets.map((market) => (
              <MarketRow market={market} key={market.title} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const MarketRow = ({ market }: { market: EventMarket }) => {
  const { seeMarket } = useSeeMarket({
    sourceCurrency: market.source_currency_code,
    targetCurrency: market.target_currency_code,
  })
  const dispatch = useAppDispatch()
  const tickerData = useAppSelector((state) =>
    getTickerData(state, market.source_currency_code, market.target_currency_code),
  )
  const [percentRate, changePercentRate] = useState(0)
  const klines = useAppSelector((state) => selectKlinesForMarket(state, market.id))
  let sparklineData = klines?.data.map((k) => k.close) ?? []
  sparklineData.reverse()

  useEffect(() => {
    if (tickerData.close) {
      const percent_rate = ((tickerData.close - tickerData.open) / tickerData.close) * 100
      changePercentRate(Math.round(100 * percent_rate) / 100)
    }
  }, [tickerData.close, tickerData.open])

  useEffect(() => {
    if (!klines) {
      dispatch(setKlines(market.id))
    }
    dispatch(
      subscribeToPublicService({
        event: Events.GetRate,
        params: {
          component: 'MarketRow',
          source_currency: market.source_currency_code,
          target_currency: market.target_currency_code,
        },
      }),
    )

    return () => {
      dispatch(
        unsubscribeToPublicService({
          event: Events.GetRate,
          params: {
            component: 'MarketRow',
            source_currency: market.source_currency_code,
            target_currency: market.target_currency_code,
          },
        }),
      )
    }
  }, [market.source_currency_code, market.target_currency_code])

  return (
    <div className={s.market} onClick={() => seeMarket()}>
      <div className={s.flagContainer}>
        <MarketIcon symbol={market.symbol} className={s.flagIcon} />
      </div>
      <div className={s.name}>{market.title}</div>
      {klines && <SparkLine sparklineData={sparklineData} percentRate={percentRate} />}
      <div className={s.right}>
        <TickerPrice price={tickerData.ask}>{tickerData.ask}</TickerPrice>
        <div className={cn(s.percent, percentRate < 0 && s.negative)}>{percentRate}%</div>
      </div>
    </div>
  )
}
